import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { Plus, X, Search, Pencil, Trash2, ExternalLink, Upload, Download } from "lucide-react";
import { sdgLabel } from "../lib/sdg";
import { FunderMatchImportCard } from "../components/admin/FunderMatchImportCard";

const TYPES = ["Equity", "Debt", "Mezzanine", "Grant"];
const SECTORS = ["Fintech", "Agritech", "Healthtech", "Cleantech", "Logistics", "E-commerce", "Manufacturing", "Edtech", "Hospitality"];
const COUNTRIES = ["Nigeria", "Kenya", "Ghana", "South Africa", "Egypt", "Rwanda", "Senegal", "Ethiopia", "Tanzania", "Uganda", "Uganda", "Côte d'Ivoire", "Mali", "Namibia", "Botswana"];
const STAGES = ["Idea", "MVP", "Revenue", "Growth"];
const SDGS = ["SDG 2", "SDG 3", "SDG 4", "SDG 5", "SDG 7", "SDG 8", "SDG 9", "SDG 13"];

const ipt = "w-full rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 outline-none focus:border-[#F59E0B]";

const blank = () => ({
  name: "", type: "Equity", sectors: [], countries: [],
  ticket_min: 100000, ticket_max: 1000000, stages: [], sdgs: [],
  website: "", contact_email: "", contact_name: "", notes: "",
});

function MultiChips({ options, value, onChange, labelFor, testid }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map(o => (
        <button key={o} type="button" onClick={() => toggle(o)}
          className={"rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors " +
            (value.includes(o)
              ? "border-[#F59E0B] bg-[#F59E0B]/20 text-[#F59E0B]"
              : "border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-neutral-700")}>
          {labelFor ? labelFor(o) : o}
        </button>
      ))}
    </div>
  );
}

export default function AdminFunders() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ type: "", sector: "", country: "", q: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (filter.type) params.type = filter.type;
    if (filter.sector) params.sector = filter.sector;
    if (filter.country) params.country = filter.country;
    if (filter.q) params.q = filter.q;
    const { data } = await api.get("/admin/funders", { params });
    setRows(data || []);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null); setForm(blank()); setErr(""); setModalOpen(true);
  };
  const openEdit = (f) => {
    setEditing(f.funder_id);
    setForm({
      name: f.name || "", type: f.type || "Equity",
      sectors: f.sectors || [], countries: f.countries || [],
      ticket_min: f.ticket_min || 0, ticket_max: f.ticket_max || 0,
      stages: f.stages || [], sdgs: f.sdgs || [],
      website: f.website || "", contact_email: f.contact_email || "",
      contact_name: f.contact_name || "", notes: f.notes || "",
    });
    setErr(""); setModalOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) { setErr("Name required"); return; }
    setBusy(true); setErr("");
    try {
      const payload = { ...form,
        ticket_min: Number(form.ticket_min) || 0,
        ticket_max: Number(form.ticket_max) || 0,
      };
      if (editing) {
        await api.put(`/admin/funders/${editing}`, payload);
      } else {
        await api.post(`/admin/funders`, payload);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this funder from the directory?")) return;
    await api.delete(`/admin/funders/${id}`);
    await load();
  };

  return (
    <AdminShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Funder directory</h1>
          <p className="mt-1 text-sm text-neutral-400">Funders across Africa — feeds matching for all SMEs.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => { setBulkResult(null); setBulkOpen(true); }}
            data-testid="admin-funder-bulk-upload"
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm font-semibold text-neutral-200 hover:bg-neutral-800">
            <Upload className="h-4 w-4"/> Bulk upload
          </button>
          <button onClick={openNew} data-testid="admin-funder-new"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308]">
            <Plus className="h-4 w-4"/> Add funder
          </button>
        </div>
      </div>
      <FunderMatchImportCard />

      {/* Filters */}
      <div className="mt-6 grid grid-cols-1 gap-2 md:grid-cols-4">
        <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2">
          <Search className="h-4 w-4 text-neutral-500"/>
          <input data-testid="admin-funder-search" value={filter.q} onChange={e => setFilter({...filter, q: e.target.value})}
            placeholder="Search by name" className="w-full bg-transparent text-sm outline-none placeholder:text-neutral-600"/>
        </div>
        <select value={filter.type} onChange={e => setFilter({...filter, type: e.target.value})} className={ipt} data-testid="admin-funder-filter-type">
          <option value="">All types</option>
          {TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={filter.sector} onChange={e => setFilter({...filter, sector: e.target.value})} className={ipt} data-testid="admin-funder-filter-sector">
          <option value="">All sectors</option>
          {SECTORS.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filter.country} onChange={e => setFilter({...filter, country: e.target.value})} className={ipt} data-testid="admin-funder-filter-country">
          <option value="">All countries</option>
          {COUNTRIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>

      {/* Funders table */}
      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-800 bg-neutral-900">
        <table className="w-full text-sm" data-testid="admin-funder-table">
          <thead className="border-b border-neutral-800 bg-neutral-950 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="sticky left-0 z-20 w-[220px] min-w-[220px] bg-neutral-950 px-4 py-3 font-semibold shadow-[6px_0_10px_-6px_rgba(0,0,0,0.85)]">Name</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Sectors</th>
              <th className="px-4 py-3 font-semibold">Countries</th>
              <th className="px-4 py-3 font-semibold">Ticket</th>
              <th className="px-4 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500">No funders match these filters.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.funder_id} className="group border-b border-neutral-800 hover:bg-neutral-800/40" data-testid={`admin-funder-row-${r.funder_id}`}>
                <td className="sticky left-0 z-10 w-[220px] min-w-[220px] max-w-[220px] bg-neutral-900 px-4 py-3 shadow-[6px_0_10px_-6px_rgba(0,0,0,0.85)] group-hover:bg-[#1c1c1c]">
                  <p className="font-semibold text-neutral-100">{r.name}</p>
                  {r.website && (
                    <a href={r.website} target="_blank" rel="noreferrer" className="text-xs text-neutral-500 hover:text-[#F59E0B] inline-flex items-center gap-1">
                      {r.website.replace(/^https?:\/\//, '').slice(0, 28)} <ExternalLink className="h-3 w-3"/>
                    </a>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="rounded bg-[#F59E0B]/15 px-2 py-0.5 text-[11px] font-bold text-[#F59E0B]">{r.type}</span>
                </td>
                <td className="px-4 py-3 text-neutral-300">{(r.sectors || []).slice(0, 3).join(", ") || "Any"}</td>
                <td className="px-4 py-3 text-neutral-300">{(r.countries || []).slice(0, 3).join(", ") || "Any"}</td>
                <td className="px-4 py-3 text-neutral-300">
                  ${((r.ticket_min || 0)/1000).toFixed(0)}K–${((r.ticket_max || 0)/1000).toFixed(0)}K
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => openEdit(r)} className="rounded p-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100" data-testid={`admin-funder-edit-${r.funder_id}`}>
                      <Pencil className="h-4 w-4"/>
                    </button>
                    <button onClick={() => remove(r.funder_id)} className="rounded p-1.5 text-red-400 hover:bg-red-950/40" data-testid={`admin-funder-delete-${r.funder_id}`}>
                      <Trash2 className="h-4 w-4"/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="admin-funder-modal">
          <div className="w-full max-w-3xl rounded-2xl border border-neutral-800 bg-neutral-950 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-bold">{editing ? "Edit funder" : "Add funder"}</h3>
              <button onClick={() => setModalOpen(false)} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-800">
                <X className="h-5 w-5"/>
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Name</label>
                <input data-testid="admin-funder-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={ipt + " mt-1"}/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Type</label>
                <select data-testid="admin-funder-type" value={form.type} onChange={e => setForm({...form, type: e.target.value})} className={ipt + " mt-1"}>
                  {TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Website</label>
                <input value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://" className={ipt + " mt-1"}/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Ticket min (USD)</label>
                <input data-testid="admin-funder-tmin" type="number" value={form.ticket_min} onChange={e => setForm({...form, ticket_min: e.target.value})} className={ipt + " mt-1"}/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Ticket max (USD)</label>
                <input data-testid="admin-funder-tmax" type="number" value={form.ticket_max} onChange={e => setForm({...form, ticket_max: e.target.value})} className={ipt + " mt-1"}/>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Sectors</label>
                <div className="mt-1"><MultiChips options={SECTORS} value={form.sectors} onChange={v => setForm({...form, sectors: v})} testid="admin-funder-sectors"/></div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Countries</label>
                <div className="mt-1"><MultiChips options={COUNTRIES} value={form.countries} onChange={v => setForm({...form, countries: v})} testid="admin-funder-countries"/></div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Stages</label>
                <div className="mt-1"><MultiChips options={STAGES} value={form.stages} onChange={v => setForm({...form, stages: v})} testid="admin-funder-stages"/></div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Impact areas</label>
                <div className="mt-1"><MultiChips options={SDGS} value={form.sdgs} onChange={v => setForm({...form, sdgs: v})} labelFor={sdgLabel} testid="admin-funder-sdgs"/></div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Contact name</label>
                <input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} className={ipt + " mt-1"}/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Contact email</label>
                <input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} className={ipt + " mt-1"}/>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Notes</label>
                <textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className={ipt + " mt-1"}/>
              </div>
            </div>
            {err && <p className="mt-3 text-sm text-red-400">{String(err)}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModalOpen(false)} className="rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-2 text-sm font-semibold text-neutral-300 hover:bg-neutral-800">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="admin-funder-save"
                className="rounded-lg bg-[#F59E0B] px-5 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
                {busy ? "Saving…" : "Save funder"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkOpen && (
        <BulkUploadModal
          onClose={() => setBulkOpen(false)}
          busy={bulkBusy}
          setBusy={setBulkBusy}
          result={bulkResult}
          setResult={setBulkResult}
          onSuccess={load}
        />
      )}
    </AdminShell>
  );
}

function BulkUploadModal({ onClose, busy, setBusy, result, setResult, onSuccess }) {
  const [file, setFile] = useState(null);
  const [dedupeBy, setDedupeBy] = useState("none");

  const downloadTemplate = async () => {
    const res = await api.get("/admin/funders/template.csv", { responseType: "blob" });
    const blob = new Blob([res.data], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "clinq-funders-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dedupe_by", dedupeBy);
      const { data } = await api.post("/admin/funders/bulk-upload", fd);
      setResult(data);
      if ((data.inserted || 0) + (data.updated || 0) > 0) onSuccess();
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Upload failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="admin-funder-bulk-modal">
      <div className="w-full max-w-xl rounded-2xl border border-neutral-800 bg-neutral-950 p-6">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-bold">Bulk upload funders</h3>
          <button onClick={onClose} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-800">
            <X className="h-5 w-5"/>
          </button>
        </div>
        <p className="mt-2 text-sm text-neutral-400">
          Upload a CSV. Required columns: <code className="text-neutral-200">name</code> (or{" "}
          <code className="text-neutral-200">Company</code>),{" "}
          <code className="text-neutral-200">type</code> (or{" "}
          <code className="text-neutral-200">Capital Structure</code>). Multi-value fields
          (sectors, countries, stages, sdgs) use <b>;</b> as separator.{" "}
          <code className="text-neutral-200">Ticket Size</code> accepts <code className="text-neutral-200">$100k-$1M</code> style values.
        </p>
        <button
          onClick={downloadTemplate}
          data-testid="admin-funder-bulk-template"
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-neutral-300 hover:bg-neutral-800">
          <Download className="h-3.5 w-3.5"/> Download template
        </button>

        <label className="mt-4 block">
          <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400">CSV file</span>
          <input
            type="file"
            accept=".csv,text/csv"
            data-testid="admin-funder-bulk-file"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            className="mt-1 block w-full text-sm text-neutral-300 file:mr-3 file:rounded-lg file:border-0 file:bg-neutral-800 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-neutral-100 hover:file:bg-neutral-700"
          />
        </label>

        <label className="mt-4 block">
          <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400">When a row already exists</span>
          <select
            value={dedupeBy}
            onChange={(e) => setDedupeBy(e.target.value)}
            data-testid="admin-funder-bulk-dedupe"
            className="mt-1 block w-full rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 focus:border-[#F59E0B] focus:outline-none"
          >
            <option value="none">Always insert a new row (may create duplicates)</option>
            <option value="name">Update existing — match by name (case-insensitive)</option>
            <option value="name+website">Update existing — match by name + website</option>
          </select>
        </label>

        {result && (
          <div className="mt-4 rounded-lg border border-neutral-800 bg-neutral-900 p-3 text-sm" data-testid="admin-funder-bulk-result">
            {result.error ? (
              <p className="text-red-400">{result.error}</p>
            ) : (
              <>
                <p className="text-emerald-300">
                  Inserted {result.inserted} funder{result.inserted === 1 ? "" : "s"}
                  {(result.updated || 0) > 0 && (
                    <span className="ml-1 text-sky-300">
                      · Updated {result.updated} existing
                    </span>
                  )}.
                </p>
                {result.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold text-amber-300">Skipped {result.errors.length} row(s):</p>
                    <ul className="mt-1 max-h-40 overflow-y-auto text-xs text-neutral-400">
                      {result.errors.map((e, i) => (
                        <li key={`err-${e.row}-${(e.error || "").slice(0, 24)}`}>Row {e.row}: {e.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-2 text-sm font-semibold text-neutral-300 hover:bg-neutral-800">
            Close
          </button>
          <button
            onClick={submit}
            disabled={!file || busy}
            data-testid="admin-funder-bulk-submit"
            className="rounded-lg bg-[#F59E0B] px-5 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
